/**
 * Sound notification utility
 * Plays professional iPhone-style notification sounds for order status changes
 */

// Shared AudioContext for better stability and performance
let sharedAudioContext = null;
let audioInitialized = false;

/**
 * Get or create a shared AudioContext
 * Ensures audio is always available and handles browser restrictions
 */
function getAudioContext() {
  if (!sharedAudioContext) {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) {
        return null;
      }
      
      sharedAudioContext = new AudioContextClass({
        sampleRate: 44100,
      });
      
      // Resume context if suspended (required by some browsers)
      if (sharedAudioContext.state === 'suspended') {
        sharedAudioContext.resume().catch((err) => {
          console.warn('Could not resume audio context:', err);
        });
      }
      
      audioInitialized = true;
    } catch (error) {
      console.warn('Could not create audio context:', error);
      return null;
    }
  }
  
  // Always try to resume if suspended
  if (sharedAudioContext && sharedAudioContext.state === 'suspended') {
    sharedAudioContext.resume().catch(() => {
      // Ignore resume errors
    });
  }
  
  return sharedAudioContext;
}

/**
 * Initialize audio on user interaction (required by some browsers)
 * Call this once when user first interacts with the page
 */
export function initializeAudio() {
  if (audioInitialized) return;
  
  const context = getAudioContext();
  if (context && context.state === 'suspended') {
    context.resume().then(() => {
      audioInitialized = true;
    }).catch(() => {
      // Ignore errors
    });
  }
}

// Auto-initialize on first user interaction
if (typeof window !== 'undefined') {
  const initAudio = () => {
    initializeAudio();
    // Remove listeners after first interaction
    window.removeEventListener('click', initAudio);
    window.removeEventListener('touchstart', initAudio);
    window.removeEventListener('keydown', initAudio);
  };
  
  window.addEventListener('click', initAudio, { once: true });
  window.addEventListener('touchstart', initAudio, { once: true });
  window.addEventListener('keydown', initAudio, { once: true });
}

/**
 * Plays a professional iPhone-style notification sound for order approval
 * Inspired by iOS notification sounds - clean, crisp, and professional
 * Uses Web Audio API to generate a high-quality tri-tone notification
 */
export function playDingSound() {
  const audioContext = getAudioContext();
  if (!audioContext) {
    console.warn('Audio context not available');
    return;
  }
  
  try {
    // Ensure context is running
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {
        // Continue anyway - might work
      });
    }
    
    const now = audioContext.currentTime;
    
    // Create master gain for overall volume control
    const masterGain = audioContext.createGain();
    masterGain.gain.setValueAtTime(0.5, now);
    masterGain.connect(audioContext.destination);
    
    // Approval sound: ASCENDING tones (low to high) - creates positive, uplifting feeling
    // Starts from lower notes and rises up - opposite of rejection sound
    const notes = [
      { 
        freq: 523.25,     // C5 - Starting note (lower)
        delay: 0, 
        duration: 0.12,
        volume: 0.7
      },
      { 
        freq: 659.25,     // E5 - Rising note
        delay: 0.06, 
        duration: 0.12,
        volume: 0.65
      },
      { 
        freq: 784.0,      // G5 - Higher note
        delay: 0.12, 
        duration: 0.12,
        volume: 0.6
      },
      { 
        freq: 1046.5,     // C6 - Highest note (ascending)
        delay: 0.18, 
        duration: 0.1,
        volume: 0.55
      },
    ];
    
    // Create and play each note with precise timing
    notes.forEach((note) => {
      try {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(masterGain);
        
        // Use 'sine' wave for clean, pure tone (iPhone uses sine waves)
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(note.freq, now);
        
        const startTime = now + note.delay;
        const endTime = startTime + note.duration;
        
        // iPhone-style envelope: very quick attack, smooth decay
        // Attack: instant (0ms) - characteristic of iOS notifications
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(note.volume, startTime + 0.001); // Instant attack
        
        // Smooth exponential decay - very clean fade out
        gainNode.gain.exponentialRampToValueAtTime(0.001, endTime);
        
        // Slight frequency rise for uplifting sound (very subtle upward motion)
        oscillator.frequency.exponentialRampToValueAtTime(
          note.freq * 1.005,
          endTime
        );
        
        oscillator.start(startTime);
        oscillator.stop(endTime);
      } catch (noteError) {
        console.warn('Error creating note:', noteError);
        // Continue with other notes
      }
    });
    
  } catch (error) {
    console.error('Error playing approval sound:', error);
    // Try fallback
    try {
      playSimpleApprovalSound();
    } catch (fallbackError) {
      console.warn('Fallback audio also failed');
    }
  }
}

/**
 * Simple fallback approval sound
 */
function playSimpleApprovalSound() {
  const audioContext = getAudioContext();
  if (!audioContext) return;
  
  try {
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {});
    }
    
    const now = audioContext.currentTime;
    
    const osc1 = audioContext.createOscillator();
    const osc2 = audioContext.createOscillator();
    const gain1 = audioContext.createGain();
    const gain2 = audioContext.createGain();
    
    osc1.connect(gain1);
    osc2.connect(gain2);
    gain1.connect(audioContext.destination);
    gain2.connect(audioContext.destination);
    
    // First note (lower - ascending)
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(523.25, now);
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.5, now + 0.001);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
    osc1.start(now);
    osc1.stop(now + 0.12);
    
    // Second note (higher - ascending)
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1046.5, now + 0.12);
    gain2.gain.setValueAtTime(0, now + 0.12);
    gain2.gain.linearRampToValueAtTime(0.4, now + 0.121);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
    osc2.start(now + 0.12);
    osc2.stop(now + 0.22);
  } catch (error) {
    console.warn('Simple approval sound failed:', error);
  }
}

/**
 * Plays a different notification sound for order rejection
 * Uses lower, descending tones to convey a negative/rejection feeling
 * Creates a distinct sound that's clearly different from approval sound
 */
export function playRejectionSound() {
  const audioContext = getAudioContext();
  if (!audioContext) {
    console.warn('Audio context not available');
    return;
  }
  
  try {
    // Ensure context is running
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {
        // Continue anyway - might work
      });
    }
    
    const now = audioContext.currentTime;
    
    // Create master gain for overall volume control
    const masterGain = audioContext.createGain();
    masterGain.gain.setValueAtTime(0.5, now);
    masterGain.connect(audioContext.destination);
    
    // Rejection sound: DESCENDING tones (high to low) - creates negative, somber feeling
    // Starts from higher notes and falls down - opposite of approval sound
    // Uses slower timing and lower volumes for a more serious tone
    const notes = [
      { 
        freq: 1046.5,     // C6 - Starting note (high - opposite of approval)
        delay: 0, 
        duration: 0.15,
        volume: 0.6
      },
      { 
        freq: 784.0,      // G5 - Descending note
        delay: 0.1, 
        duration: 0.16,
        volume: 0.5
      },
      { 
        freq: 523.25,     // C5 - Lower note (descending)
        delay: 0.2, 
        duration: 0.18,
        volume: 0.45
      },
      { 
        freq: 392.0,      // G4 - Lowest note (deep descending)
        delay: 0.3, 
        duration: 0.2,
        volume: 0.4
      },
    ];
    
    // Create and play each note with precise timing
    notes.forEach((note) => {
      try {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(masterGain);
        
        // Use 'sine' wave for clean tone
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(note.freq, now);
        
        const startTime = now + note.delay;
        const endTime = startTime + note.duration;
        
        // Slower attack for more serious tone
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(note.volume, startTime + 0.005);
        
        // Slower exponential decay for somber feeling
        gainNode.gain.exponentialRampToValueAtTime(0.001, endTime);
        
        // More pronounced frequency drop for descending, negative feeling
        oscillator.frequency.exponentialRampToValueAtTime(
          note.freq * 0.95,
          endTime
        );
        
        oscillator.start(startTime);
        oscillator.stop(endTime);
      } catch (noteError) {
        console.warn('Error creating rejection note:', noteError);
        // Continue with other notes
      }
    });
    
  } catch (error) {
    console.error('Error playing rejection sound:', error);
    // Try fallback
    try {
      playSimpleRejectionSound();
    } catch (fallbackError) {
      console.warn('Fallback rejection sound also failed');
    }
  }
}

/**
 * Simple fallback rejection sound
 */
function playSimpleRejectionSound() {
  const audioContext = getAudioContext();
  if (!audioContext) return;
  
  try {
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {});
    }
    
    const now = audioContext.currentTime;
    
    const osc1 = audioContext.createOscillator();
    const osc2 = audioContext.createOscillator();
    const gain1 = audioContext.createGain();
    const gain2 = audioContext.createGain();
    
    osc1.connect(gain1);
    osc2.connect(gain2);
    gain1.connect(audioContext.destination);
    gain2.connect(audioContext.destination);
    
    // First note (high - descending)
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(1046.5, now);
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.5, now + 0.005);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
    osc1.start(now);
    osc1.stop(now + 0.15);
    
    // Second note (low - descending)
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(392.0, now + 0.2);
    gain2.gain.setValueAtTime(0, now + 0.2);
    gain2.gain.linearRampToValueAtTime(0.4, now + 0.205);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    osc2.start(now + 0.2);
    osc2.stop(now + 0.4);
  } catch (error) {
    console.warn('Simple rejection sound failed:', error);
  }
}
